/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */
import React, { useState } from 'react';
import { uid } from 'react-uid';
import QueryString from 'qs';
import  Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import { string, shape, array } from 'prop-types';
import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { Link, useLocation } from 'react-router-dom';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ReactMarkdown from 'react-markdown';
import ExpansionPanelDetails  from '@material-ui/core/ExpansionPanelDetails';
import ReactHTMLParser from 'react-html-parser';
import { useDispatch } from 'react-redux';
import GraphqlSeoHeadTags from '../../GraphqlCommonComponents/GraphqlSeoHeadTags/GraphqlSeoHeadTags';
import { trackEvent } from '../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

const useStyles = makeStyles((theme) => ({
    container: {
        [theme.breakpoints.down('sm')]: {
            maxWidth: theme.palette.tiers.container.maxWidth.downSmall,
            margin: '0 auto',
        },
        [theme.breakpoints.down('md')]: {
            maxWidth: theme.palette.tiers.container.maxWidth.downMedium,
            margin: '0 auto',
            padding: 15,
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: 980,
            margin: '0 auto',
        },
        [theme.breakpoints.up('lg')]: {
            maxWidth: 980,
            margin: '0 auto',
            padding: 50,
            marginTop: 40,
            border: `1px solid ${theme.palette.grey10}`,
        },
    },
    breadCrumb: {
        marginTop: 10,
        '& a': {
            color: theme.palette.common.black,
            display: 'flex',
            alignItems: 'center',
            textDecoration: 'none',
            fontSize: 15,
            outline: 'none',
        },
    },
    panelDescription: {
        borderBottom: `1px solid ${theme.palette.grey10}`,

    },
    contentQuestion: {
        '& h3': {
            display: 'block',
            margin: '.45em 0',
        },
    },
    title: {
        fontSize: 16,
        fontWeight: 'bold',
    },
    header: {
        '& h1': {
            textAlign: 'center',
            fontWeight: 'normal',
            fontSize: 40,
            margin: '1em 0',
        },
    },
    questionAnswer: {
        '& ul': {
            margin: '.35em 0 10px 20px',
        },
        '& ol': {
            margin: '.35em 0 10px 20px',
        },
    },
}));

// Dynamically get label by brand
const getPanelLabel = (category = {}, index) => {
    const label = category.id || category.category_title || category._metadata?.uid || `panel_${index}`;
    const words = String(label)
        .replace(/&/g, ' and ')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .match(/[a-zA-Z0-9]+/g);

    if (!words?.length) {
        return `panel_${index}`;
    }

    return words.reduce((panelLabel, word, wordIndex) => {
        const normalizedWord = word.toLowerCase();
        if (wordIndex === 0) {
            return normalizedWord;
        }
        return `${panelLabel}${normalizedWord.charAt(0).toUpperCase()}${normalizedWord.slice(1)}`;
    }, '');
};

const FrequentlyAskedQuestionsUI = ({ faqData }) => {
    const urlParams = useLocation().search?.slice(1) || '';
    const queryParams = QueryString.parse(urlParams);
    const activeTab = queryParams?.['active-tab'] || queryParams?.scrollTo || '';

    const [expandedPanelLabel, setExpandedPanelLabel] = useState(activeTab ? getPanelLabel({ id: activeTab }) : '');
    const dispatch = useDispatch();

    const handlePanelPress = (panelLabel) => (_, isExpanded) => {
        setExpandedPanelLabel(isExpanded ? panelLabel : '');
        dispatch(trackEvent({
            eventAction: 'FAQ-ACCORDION',
            eventCategory: 'FAQ-ACCORDION',
            ga_event_name: 'accordion_interaction',
            interaction_type: isExpanded ? 'expand' : 'collapse',
            accordion_item: panelLabel,
            accordion_group: expandedPanelLabel,

        }));
    };

    const classes = useStyles(faqData);
    const title = faqData?.title;
    const seo = faqData?.seo ?? {};

    const linkOrReference = ({ href, children }) => <a href={href}>{children}</a>;

    return (
        <>
            <GraphqlSeoHeadTags seoData={seo} />
            <div className={classes.container}>
                <div className={classes.breadCrumb}><Link to="/customer-service"><ArrowBackIosIcon className={classes.arrowSign} />Help Center</Link></div>
                <Grid className={classes.header} item xs={12}>
                    <div className={classes.title}>
                        <div className={classes.faqtitle}><h1>{title}</h1></div>
                    </div>
                </Grid>
                {faqData?.categories?.length && faqData.categories?.map((d, panelIndex) => {
                    const panelLabel = getPanelLabel(d.category, panelIndex);
                    return (
                        <ExpansionPanel
                            key={d.category.category_title}
                            expanded={expandedPanelLabel === panelLabel}
                            onChange={handlePanelPress(panelLabel)}
                        >

                            <ExpansionPanelSummary className={classes.panelDescription} expandIcon={<ExpandMoreIcon />}>
                                <div classes={classes.catTitle}><h2>{d.category.category_title}</h2>
                                </div>
                            </ExpansionPanelSummary>
                            <ExpansionPanelDetails>
                                <div>
                                    {d.category.entries.map((q, index) => (
                                        <div key={uid(q, index)} className={classes.contentQuestion}>
                                            <h3>{ReactHTMLParser(q.entry.question)}</h3>
                                            <div className={classes.contentAnswer}>
                                                <ReactMarkdown
                                                    renderers={{
                                                        linkReference: linkOrReference,
                                                    }}
                                                    source={q.entry.answer}
                                                    className={classes.questionAnswer}
                                                    escapeHtml
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </ExpansionPanelDetails>
                        </ExpansionPanel>
                    );
                })}
            </div>
        </>
    );
};

FrequentlyAskedQuestionsUI.propTypes = {
    faqData: shape({
        title: string.isRequired,
        categories: array.isRequired,
        seo: shape({
            page_title: string,
        }),
    }).isRequired,
};
export default FrequentlyAskedQuestionsUI;
